import type { types as Mediasoup } from "mediasoup-client";

import { HISTORY_LEN, resolveMutedLevel, scoreLinkLevel } from "./level-scale";
import type { ResolvedLinkLevel } from "./level-scale";
import { isCandidatePairStats, isInboundRtpVideoStats } from "./rtc-stats-entry";
import type { CandidatePairStats, RtcStatsEntry } from "./rtc-stats-entry";

/** Snapshot of connection-quality telemetry exposed to the UI. */
export interface ConnectionSnapshot {
  linkLevel: ResolvedLinkLevel;
  rttMs: number | null;
  lossPercentRecent: number | null;
  packetsLostRecent: number;
  transportState: string;
  receivedAt?: number;
}

/**
 * Typed contract for a `createConnectionQualityMonitor()` instance. One per session —
 * inject a longer-lived instance via `createTeleopSession`'s `connectionQuality` option
 * to survive reconnects (see `useOperatorSession.ts`).
 */
export interface ConnectionQualityMonitor {
  startConnectionQualityMonitor: (
    transport: Mediasoup.Transport,
    getStats: () => Promise<Map<string, RtcStatsEntry>>
  ) => void;
  stopConnectionQualityMonitor: () => void;
  getConnectionSnapshot: () => ConnectionSnapshot;
  getConnectionHistory: () => ConnectionSnapshot[];
  onConnectionSnapshot: (fn: (snapshot: ConnectionSnapshot) => void) => () => void;
}

const POLL_MS = 2000;
/** Minimum packets over the interval before loss-rate is meaningful (noise on small samples). */
const MIN_PACKETS_FOR_RATE = 30;

/**
 * Create an independent connection-quality monitor. One per `createTeleopSession()`
 * call (see teleop-session/index.ts) — never a module-level global, so two independent
 * sessions in the same tab (e.g. a split-screen view) each track their OWN transport's
 * RTT/loss/transportState instead of one clobbering the other's poll timer and stats
 * provider.
 */
export function createConnectionQualityMonitor(): ConnectionQualityMonitor {
  const snapshot: ConnectionSnapshot = {
    linkLevel: "unknown",
    rttMs: null,
    lossPercentRecent: null,
    packetsLostRecent: 0,
    transportState: "new",
  };

  let history: ConnectionSnapshot[] = [];

  const listeners = new Set<(s: ConnectionSnapshot) => void>();

  let statsProvider: (() => Promise<Map<string, RtcStatsEntry>>) | null = null;

  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const prevInboundById = new Map<string, { packetsLost: number; packetsReceived: number }>();

  function startConnectionQualityMonitor(
    transport: Mediasoup.Transport,
    getStats: () => Promise<Map<string, RtcStatsEntry>>
  ) {
    statsProvider = getStats;
    prevInboundById.clear();
    transport.on("connectionstatechange", (state) => {
      snapshot.transportState = String(state);
      refreshLinkScore(snapshot.lossPercentRecent != null);
      notify();
    });
    snapshot.transportState = transport.connectionState || "new";
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = setInterval(() => {
      void pollStats();
    }, POLL_MS);
    void pollStats();
  }

  function stopConnectionQualityMonitor() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    statsProvider = null;
    prevInboundById.clear();
  }

  async function pollStats() {
    if (!statsProvider) return;
    try {
      const stats = await statsProvider();
      let candidatePair: CandidatePairStats | null = null;
      let fallbackPair: CandidatePairStats | null = null;
      let worstLossPct = 0;
      let worstDeltaLost = 0;
      let hasIntervalData = false;
      const currentIds = new Set<string>();

      // A for..of loop (not stats.forEach(callback)) — assignments to candidatePair/
      // fallbackPair/hasIntervalData/worst* below need to stay in THIS function's flow
      // analysis, not a separate closure, so a later read sees the narrowed type instead
      // of being widened back to the declaration.
      for (const [id, r] of stats) {
        if (isInboundRtpVideoStats(r)) {
          currentIds.add(id);
          const lost = r.packetsLost || 0;
          const recv = r.packetsReceived || 0;
          const prev = prevInboundById.get(id);
          if (prev) {
            const dLost = Math.max(0, lost - prev.packetsLost);
            const dRecv = Math.max(0, recv - prev.packetsReceived);
            const total = dLost + dRecv;
            if (total >= MIN_PACKETS_FOR_RATE) {
              hasIntervalData = true;
              const pct = (dLost / total) * 100;
              if (pct > worstLossPct) worstLossPct = pct;
              if (dLost > worstDeltaLost) worstDeltaLost = dLost;
            }
          }
          prevInboundById.set(id, { packetsLost: lost, packetsReceived: recv });
        }
        if (isCandidatePairStats(r) && r.state === "succeeded" && r.currentRoundTripTime != null) {
          if (r.nominated) candidatePair = r;
          else fallbackPair = r;
        }
      }

      for (const id of prevInboundById.keys()) {
        if (!currentIds.has(id)) prevInboundById.delete(id);
      }

      const pair = candidatePair || fallbackPair;
      if (pair?.currentRoundTripTime != null) {
        snapshot.rttMs = Math.round(pair.currentRoundTripTime * 1000);
      }

      snapshot.lossPercentRecent = hasIntervalData ? worstLossPct : null;
      snapshot.packetsLostRecent = worstDeltaLost;

      refreshLinkScore(hasIntervalData);
      notify();
    } catch {
      /* consumer may be closed */
    }
  }

  /**
   * Level scoring: worst(transport, loss, rtt).
   * framesDropped is not used — the browser accumulates it and over-reports with two streams.
   */
  function refreshLinkScore(hasIntervalData: boolean) {
    const scored = scoreLinkLevel(
      {
        transportState: snapshot.transportState,
        lossPercentRecent: snapshot.lossPercentRecent,
        rttMs: snapshot.rttMs,
      },
      hasIntervalData
    );
    snapshot.linkLevel = resolveMutedLevel(scored);
    snapshot.receivedAt = Date.now();
  }

  function notify() {
    const copy = { ...snapshot };
    history = [...history, copy].slice(-HISTORY_LEN);
    listeners.forEach((fn) => fn(copy));
  }

  function getConnectionSnapshot(): ConnectionSnapshot {
    return { ...snapshot };
  }

  /** Last N snapshots (oldest → newest). */
  function getConnectionHistory(): ConnectionSnapshot[] {
    return history.slice();
  }

  function onConnectionSnapshot(fn: (s: ConnectionSnapshot) => void) {
    listeners.add(fn);
    fn({ ...snapshot });
    return () => listeners.delete(fn);
  }

  return {
    startConnectionQualityMonitor,
    stopConnectionQualityMonitor,
    getConnectionSnapshot,
    getConnectionHistory,
    onConnectionSnapshot,
  };
}
